import React, { useCallback, useMemo, useRef, useState } from "react";
import { LuCalculator, LuClock3, LuLockKeyhole, LuReceiptText, LuShieldCheck, LuTarget } from "react-icons/lu";
import { BusinessCaseApiError, requestBusinessCaseEmail, submitBusinessCaseLead } from "api/business_case/businessCaseApi";
import { BusinessCaseLeadRequest, BusinessCaseResult } from "api/business_case/businessCaseApi.types";
import StepProgramme from "./StepProgramme";
import StepContact from "./StepContact";
import StepResult, { EmailState } from "./StepResult";
import {
  ChoiceFieldName,
  Contact,
  Evidence,
  NumberFieldName,
  OBJECTIVES,
  Objective,
  Pricing,
  Programme,
  defaultProgramme,
  validateContact,
  validateProgramme,
} from "./calculator";

const STEP_LABELS = ["Your programme", "Your details", "Your business case"] as const;

const EMPTY_CONTACT: Contact = {
  name: "",
  email: "",
  organization: "",
  companySize: "",
  role: "",
  consent: false,
};

const SIDEBAR_ITEMS = [
  { Icon: LuTarget, title: "Value tied to your goal", body: (objective: Objective) => OBJECTIVES[objective].label },
  { Icon: LuClock3, title: "Admin time recovered", body: () => "Time savings shown separately." },
  { Icon: LuReceiptText, title: "An instant planning quote", body: () => "A plan based on your participant count." },
];

// A 422 carries the model's own messages. Showing the generic connection error for a rejected work
// email would send the visitor looking in entirely the wrong place.
function serverError(error: unknown): string | null {
  const messages = error instanceof BusinessCaseApiError ? error.errors : undefined;
  return messages?.length ? messages.join(" ") : null;
}

// Threads the sales inbox's two alerts for one visitor together. Nothing is persisted, so this is
// the only handle they share.
function newRequestId(): string {
  return typeof crypto?.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        return (char === "x" ? random : (random & 0x3) | 0x8).toString(16);
      });
}

type Props = {
  pricing: Pricing;
  evidence: Evidence;
};

export default function BusinessCaseBuilder({ pricing, evidence }: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [programme, setProgramme] = useState<Programme>(defaultProgramme);
  const [contact, setContact] = useState<Contact>(EMPTY_CONTACT);
  const [result, setResult] = useState<BusinessCaseResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [emailState, setEmailState] = useState<EmailState>("idle");
  const [emailError, setEmailError] = useState("");
  const [error, setError] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const stepHeadingRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef(false);
  const requestId = useRef(newRequestId());

  const updateField = useCallback((name: NumberFieldName, value: number) => {
    setProgramme((current) => ({ ...current, [name]: value }));
  }, []);

  const updateChoice = useCallback((name: ChoiceFieldName, value: number) => {
    setProgramme((current) => ({ ...current, [name]: value }));
  }, []);

  const updateObjective = useCallback((objective: Objective) => {
    setProgramme((current) => ({ ...current, objective }));
  }, []);

  const updateContact = useCallback((name: keyof Contact, value: string | boolean) => {
    setContact((current) => ({ ...current, [name]: value }));
  }, []);

  const goToStep = useCallback((next: 1 | 2 | 3) => {
    setStep(next);
    stepHeadingRef.current?.focus({ preventScroll: true });
    stepHeadingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Their contact details are kept so a second look at the figures does not mean filling the form
  // again; the sales inbox recognises the repeat by request id rather than alerting twice.
  const restart = useCallback(() => {
    setResult(null);
    setEmailState("idle");
    setEmailError("");
    setError("");
    goToStep(1);
  }, [goToStep]);

  const payload = useCallback(
    (): BusinessCaseLeadRequest => ({
      request_id: requestId.current,
      business_case_lead: {
        name: contact.name.trim(),
        email: contact.email.trim(),
        organization: contact.organization.trim(),
        company_size: contact.companySize,
        role: contact.role,
        consent: contact.consent,
        objective: programme.objective,
        participants: programme.participants,
        salary: programme.salary,
        turnover_rate: programme.turnoverRate,
        turnover_cost_pct: programme.turnoverCostPct,
        reduction_pct: programme.reductionPct,
        productivity_lift_pct: programme.productivityLiftPct,
        open_roles: programme.openRoles,
        promotion_increase_pct: programme.promotionIncreasePct,
        external_hire_premium_pct: programme.externalHirePremiumPct,
        new_hires: programme.newHires,
        ramp_days_saved: programme.rampDaysSaved,
        hourly_rate: programme.hourlyRate,
      },
      website: honeypot,
    }),
    [contact, programme, honeypot]
  );

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;

    setError("");

    const programmeError = validateProgramme(programme);
    if (programmeError) {
      setError(programmeError);
      goToStep(1);
      return;
    }

    if (step === 1) {
      goToStep(2);
      return;
    }

    const contactError = validateContact(contact);
    if (contactError) {
      setError(contactError);
      return;
    }

    inFlight.current = true;
    setSubmitting(true);

    try {
      const response = await submitBusinessCaseLead(payload());

      setResult(response.result);
      setEmailState("idle");
      goToStep(3);
    } catch (requestError) {
      setError(
        serverError(requestError) ??
          "We couldn't build your business case just now. Please check your connection and try again."
      );
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  async function handleEmailRequest() {
    if (emailState === "sending" || emailState === "sent") return;

    setEmailState("sending");
    setEmailError("");

    try {
      await requestBusinessCaseEmail(payload());
      setEmailState("sent");
    } catch (requestError) {
      setEmailState("error");
      setEmailError(
        serverError(requestError) ??
          "We couldn't send that email just now. Please try again, or book a demo and we'll bring it with us."
      );
    }
  }

  const progress = useMemo(() => (step / STEP_LABELS.length) * 100, [step]);

  return (
    <div className="tw-mx-auto tw-max-w-[1100px] tw-overflow-hidden tw-rounded-[28px] tw-border tw-border-[#c9deed] tw-bg-white tw-shadow-[0_24px_70px_rgba(23,72,111,.12)]">
      <div className="tw-border-b tw-border-[#dce8f2] tw-px-9 tw-py-5 sm:tw-px-6">
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#087bd3] tw-text-white">
            <LuCalculator aria-hidden className="tw-h-5 tw-w-5" />
          </span>
          <div className="tw-min-w-0">
            <h2 className="tw-m-0 tw-text-xl tw-font-black tw-text-[#173754] sm:tw-text-base">
              Your mentoring business case
            </h2>
            <p className="tw-m-0 tw-text-sm tw-text-[#5b7188]">
              About 2 minutes · all amounts in US dollars
            </p>
          </div>
          <span className="tw-ml-auto tw-shrink-0 tw-text-sm tw-font-black tw-text-[#087bd3] sm:tw-hidden">
            Step {step} of {STEP_LABELS.length}
          </span>
        </div>

        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          aria-label={`Step ${step} of ${STEP_LABELS.length}`}
          className="tw-relative tw-mt-4 tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-[#cfe4f5]"
        >
          <div
            className="tw-h-full tw-rounded-full tw-bg-[#087bd3] tw-transition-all tw-duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        <ol
          aria-label="Business case steps"
          className="tw-mt-3 tw-mb-0 tw-grid tw-list-none tw-grid-cols-3 tw-gap-2 tw-p-0 tw-text-sm tw-font-bold sm:tw-text-xs"
        >
          {STEP_LABELS.map((label, index) => (
            <li
              key={label}
              aria-current={step === index + 1 ? "step" : undefined}
              className={step === index + 1 ? "tw-text-[#087bd3]" : "tw-text-[#5b7188]"}
            >
              {index + 1}. {label}
            </li>
          ))}
        </ol>
      </div>

      <div
        ref={stepHeadingRef}
        tabIndex={-1}
        aria-label={`Step ${step}: ${STEP_LABELS[step - 1]}`}
        className="tw-scroll-mt-24 tw-outline-none"
      >
        <form onSubmit={handleSubmit} aria-busy={submitting} className="tw-grid tw-grid-cols-[1fr_310px] sm:tw-grid-cols-1">
          <div className="tw-relative tw-min-w-0 tw-p-9 sm:tw-p-6">
            <fieldset className="tw-m-0 tw-border-0 tw-p-0" disabled={submitting}>
              {step === 1 ? (
                <StepProgramme
                  programme={programme}
                  evidence={evidence}
                  onChange={updateField}
                  onChoiceChange={updateChoice}
                  onObjectiveChange={updateObjective}
                />
              ) : null}

              {step === 2 ? (
                <StepContact
                  contact={contact}
                  onChange={updateContact}
                  onBack={() => goToStep(1)}
                  submitting={submitting}
                  honeypot={honeypot}
                  onHoneypotChange={setHoneypot}
                />
              ) : null}

              {step === 3 && result ? (
                <StepResult
                  result={result}
                  currency={pricing.currency}
                  contact={contact}
                  emailState={emailState}
                  emailError={emailError}
                  onEmailRequest={handleEmailRequest}
                  onRestart={restart}
                />
              ) : null}

              {error ? (
                <p
                  role="alert"
                  className="tw-mt-5 tw-mb-0 tw-rounded-xl tw-border tw-border-[#e2b4b4] tw-bg-[#fdf3f3] tw-p-4 tw-text-sm tw-font-bold tw-text-[#8a2626]"
                >
                  {error}
                </p>
              ) : null}
            </fieldset>
          </div>

          <aside className="tw-border-l tw-border-t-0 tw-border-[#dce8f2] tw-bg-[#10233f] tw-p-8 tw-text-white sm:tw-border-l-0 sm:tw-border-t sm:tw-p-6">
            <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#70d9ce]">
              A case you can take to your team
            </p>

            <div className="tw-mt-7 tw-space-y-6">
              {SIDEBAR_ITEMS.map(({ Icon, title, body }) => (
                <div key={title} className="tw-flex tw-gap-3">
                  <Icon aria-hidden className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-[#70d9ce]" />
                  <div>
                    <p className="tw-m-0 tw-text-sm tw-font-black">{title}</p>
                    <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#c2d3e2]">
                      {body(programme.objective)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="tw-mt-8 tw-rounded-2xl tw-border tw-border-[#55cfc1]/35 tw-bg-[#55cfc1]/10 tw-p-5">
              <LuShieldCheck aria-hidden className="tw-h-5 tw-w-5 tw-text-[#70d9ce]" />
              <p className="tw-mt-3 tw-mb-0 tw-font-black">
                {pricing.promise.hours}-hour savings promise
              </p>
              <p className="tw-mt-2 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#c2d3e2]">
                For eligible cohorts of {pricing.promise.minimumParticipants}+ participants.
                Assessed separately from these estimates, against an agreed baseline.
              </p>
            </div>

            <p className="tw-mt-6 tw-mb-0 tw-flex tw-items-start tw-gap-2 tw-text-xs tw-leading-5 tw-text-[#c2d3e2]">
              <LuLockKeyhole aria-hidden className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
              Your results are revealed only after your contact details are submitted.
            </p>
          </aside>
        </form>
      </div>
    </div>
  );
}
